import React, { useEffect, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import apiClient from '../api/apiClient';
import PageTitle from '../elements/PageTitle';
import { useLanguage } from '../i18n/LanguageContext';

/*
 * Every active FAQ from the admin panel, in the order set there. What is
 * listed, its wording in both languages, and its position are all managed
 * under FAQs in the panel.
 */
const Faq = () => {
  const { t, language } = useLanguage();
  const { hash } = useLocation();
  const [faqs, setFaqs] = useState(undefined);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    let cancelled = false;

    apiClient.get('/homepage/faqs')
      .then(({ data }) => { if (!cancelled) setFaqs(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setFaqs([]); });

    return () => { cancelled = true; };
  }, [language]);

  // "View Answer" on a homepage card links to /faq#faq-<id>: open that
  // question and bring it into view once the list has loaded.
  useEffect(() => {
    if (!faqs?.length) return undefined;

    const linked = hash.startsWith('#faq-') ? hash.slice(1) : null;
    const target = linked && faqs.some((faq) => `faq-${faq.id}` === linked) ? linked : `faq-${faqs[0].id}`;

    setOpenKey(target);

    if (!linked) return undefined;

    const timer = window.setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [faqs, hash]);

  // Two columns on wide screens, filled left then right so the admin's order
  // reads top to bottom in each.
  const half = Math.ceil((faqs?.length ?? 0) / 2);
  const columns = faqs ? [faqs.slice(0, half), faqs.slice(half)].filter((column) => column.length) : [];

  return (
    <>
      <div className="page-content bg-white">
        <PageTitle activePage="FAQ" parentTitle="Home" />

        <section className="content-inner-1 overflow-hidden">
          <div className="container">
            {faqs === undefined && (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <p className="m-t10">{t('Loading...')}</p>
              </div>
            )}

            {faqs?.length === 0 && (
              <p className="text-center py-5">{t('No questions have been published yet.')}</p>
            )}

            <div className="row">
              {columns.map((column, columnIndex) => (
                <div className="col-lg-6" key={columnIndex}>
                  <Accordion
                    className="dz-accordion dz-accordion-skew"
                    activeKey={openKey}
                    onSelect={(key) => setOpenKey(key)}
                  >
                    {column.map((faq) => (
                      <Accordion.Item
                        className="accordion-item"
                        key={faq.id}
                        eventKey={`faq-${faq.id}`}
                        id={`faq-${faq.id}`}
                      >
                        <Accordion.Header as="h2">
                          {faq.question}
                          <span className="toggle-close"></span>
                        </Accordion.Header>

                        <Accordion.Body>
                          {(faq.tag || faq.level) && (
                            <p className="m-b10 text-primary fw-semibold small">
                              {[faq.tag, faq.level].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          {/* Line breaks typed in the panel start new paragraphs. */}
                          {(faq.answer || '').split(/\n+/).map((paragraph, index, paragraphs) => (
                            <p className={index === paragraphs.length - 1 ? 'm-b0' : 'm-b10'} key={index}>{paragraph}</p>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Faq;
